import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Users, Home, Briefcase, Heart, ArrowRight, type LucideIcon } from 'lucide-react';
import {ProviderOverviewSkeleton} from '@/features/resource-hub/components/ProviderOverviewSkeleton';

const ICON_CYCLE: LucideIcon[] = [Users, Home, Briefcase, Heart];

const getOptionIcon = (name: string, index: number): LucideIcon => {
  const lower = name.toLowerCase();
  if (lower.includes('home') || lower.includes('caregiver') || lower.includes('in-home')) return Home;
  if (lower.includes('law') || lower.includes('attorney') || lower.includes('legal') || lower.includes('financial')) return Briefcase;
  if (lower.includes('support') || lower.includes('health') || lower.includes('wellness') || lower.includes('hospice')) return Heart;
  if (lower.includes('manager') || lower.includes('coordinator') || lower.includes('specialist') || lower.includes('aging life')) return Users;
  return ICON_CYCLE[index % ICON_CYCLE.length];
};


import { JourneyProgress } from './JourneyProgress';
import { apiClient } from '@/core/api/client';

interface ProviderOverviewState {
  sessionId?: string;
  carePlanId?: string;
}

type CareOption = {
  id: string;
  category_id: string;
  name: string;
  what_they_do: string;
  why_you_need_them: string;
};

export function ProviderOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ProviderOverviewState | null;
  const sessionId = state?.sessionId;
  const carePlanId = state?.carePlanId;

  const { data: careOptions, isLoading, isError } = useQuery({
    queryKey: ['careOptions', sessionId],
    queryFn: async () => {
      const response = await apiClient.get<CareOption[]>('/care-options', {
        params: { session_id: sessionId },
      });
      return response.data;
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen">
      <JourneyProgress currentStep="overview" />

      <div className="container max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-semibold text-gray-900 mb-3">
            Your Care Team Overview
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Based on your situation, you'll need to work with several types of service providers.
            Here's what each one does and why they're important.
          </p>

          {/* Download Button */}
          {/* <div className="mt-6">
            <Button
              variant="outline"
              className="border-[#3d7872] text-[#3d7872] hover:bg-[#3d7872] hover:text-white"
            >
              <Download size={16} className="mr-2" />
              Download Provider Guide
            </Button>
          </div> */}
        </div>

        {/* Provider Type Cards */}
        <div className="space-y-6 mb-10">
          {isLoading && <ProviderOverviewSkeleton />}

          {isError && (
            <div className="text-center py-12 text-gray-500">
              Unable to load care options. Please try again.
            </div>
          )}

          {careOptions?.map((option, index) => {
            const Icon = getOptionIcon(option.name, index);
            return (
            <Card key={option.id} className="p-8 hover:shadow-lg transition-shadow">
              <div className="flex gap-6">
                 {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#3d7872] to-[#2d6862] rounded-2xl flex items-center justify-center">
                    <Icon className="text-white" size={28} />
                  </div>
                </div>

                <div className="flex-1">
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                  {option.name}
                </h2>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-[#3d7872] uppercase tracking-wide mb-2">
                      What They Do
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {option.what_they_do}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-[#3d7872] uppercase tracking-wide mb-2">
                      Why You Need Them
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {option.why_you_need_them}
                    </p>
                  </div>
                </div>
              </div>

              </div>
              
            </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-[#3d7872]/5 to-[#3d7872]/10 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            Ready to Find Your Providers?
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Now that you understand each role, we'll help you find the right professionals
            for your specific situation. We'll go through each provider type one at a time.
          </p>

          <div className="flex items-center justify-center gap-3 mb-6 text-sm text-gray-600">
            <span>{careOptions?.length ?? 4} provider types</span>
            <span>•</span>
            <span>~5 minutes</span>
            <span>•</span>
            <span>Personalized recommendations</span>
          </div>

          <Button
            onClick={() => navigate('/detailed-care-navigator/matching/0', {
              state: {
                sessionId,
                carePlanId,
              },
            })}
            className="bg-[#3d7872] hover:bg-[#2d6862] text-white px-8 py-6 text-lg rounded-xl inline-flex items-center gap-2"
            size="lg"
          >
            Start Matching Providers
            <ArrowRight size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
}
